// This file is auto-generated by @hey-api/openapi-ts

import type {
  Client,
  ClientMeta,
  Options as Options2,
  RequestResult,
  TDataShape,
} from './client';
import { client } from './client.gen';
import type {
  AddAlignmentReferencesData,
  AddAlignmentReferencesErrors,
  AddAlignmentReferencesResponses,
  AddMapWorkspaceReferencesData,
  AddMapWorkspaceReferencesErrors,
  AddMapWorkspaceReferencesResponses,
  AddMobileMappingMissionReferencesData,
  AddMobileMappingMissionReferencesErrors,
  AddMobileMappingMissionReferencesResponses,
  AddModelReferencesData,
  AddModelReferencesErrors,
  AddModelReferencesResponses,
  AddTerrestrialScanReferencesData,
  AddTerrestrialScanReferencesErrors,
  AddTerrestrialScanReferencesResponses,
  CreateCurbAndGutterBreaklineData,
  CreateCurbAndGutterBreaklineErrors,
  CreateCurbAndGutterBreaklineResponses,
  CreateJerseyBarrierBreaklineData,
  CreateJerseyBarrierBreaklineErrors,
  CreateJerseyBarrierBreaklineResponses,
  CreateRoadCrownBreaklineData,
  CreateRoadCrownBreaklineErrors,
  CreateRoadCrownBreaklineResponses,
  CreateWorkspaceData,
  CreateWorkspaceErrors,
  CreateWorkspaceResponses,
  DeleteCurbAndGutterBreaklineData,
  DeleteCurbAndGutterBreaklineErrors,
  DeleteCurbAndGutterBreaklineResponses,
  DeleteJerseyBarrierBreaklineData,
  DeleteJerseyBarrierBreaklineErrors,
  DeleteJerseyBarrierBreaklineResponses,
  DeleteReferenceData,
  DeleteReferenceErrors,
  DeleteReferenceResponses,
  DeleteRoadCrownBreaklineData,
  DeleteRoadCrownBreaklineErrors,
  DeleteRoadCrownBreaklineResponses,
  DeleteWorkspaceData,
  DeleteWorkspaceErrors,
  DeleteWorkspaceResponses,
  GetCurbAndGutterBreaklineData,
  GetCurbAndGutterBreaklineErrors,
  GetCurbAndGutterBreaklineResponses,
  GetJerseyBarrierBreaklineData,
  GetJerseyBarrierBreaklineErrors,
  GetJerseyBarrierBreaklineResponses,
  GetRoadCrownBreaklineData,
  GetRoadCrownBreaklineErrors,
  GetRoadCrownBreaklineResponses,
  GetWorkspaceData,
  GetWorkspaceErrors,
  GetWorkspaceResponses,
  HealthCheckData,
  HealthCheckErrors,
  HealthCheckResponses,
  ListBreaklinesData,
  ListBreaklinesErrors,
  ListBreaklinesResponses,
  ListReferencesData,
  ListReferencesErrors,
  ListReferencesResponses,
  ListWorkspacesData,
  ListWorkspacesErrors,
  ListWorkspacesResponses,
  UpdateWorkspaceData,
  UpdateWorkspaceErrors,
  UpdateWorkspaceResponses,
} from './types.gen';

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: keyof ClientMeta extends never ? Record<string, unknown> : ClientMeta;
};

/**
 * Health check endpoint
 *
 * Returns dependency health. Responds with 503 if any check is unhealthy.
 */
export const healthCheck = <ThrowOnError extends boolean = false>(
  options?: Options<HealthCheckData, ThrowOnError>
): RequestResult<HealthCheckResponses, HealthCheckErrors, ThrowOnError> =>
  (options?.client ?? client).get<
    HealthCheckResponses,
    HealthCheckErrors,
    ThrowOnError
  >({ url: '/health', ...options });

/**
 * List workspaces for a tenant
 *
 * ### List workspaces
 * Returns a paginated list of all FX workspaces in the tenant.
 *
 * ### Notes
 * - Use **pageIndex** and **pageSize** to paginate.
 * - Each item is a full workspace (including tenant TRN, workspace TRN, name, and audit fields).
 */
export const listWorkspaces = <ThrowOnError extends boolean = false>(
  options: Options<ListWorkspacesData, ThrowOnError>
): RequestResult<ListWorkspacesResponses, ListWorkspacesErrors, ThrowOnError> =>
  (options.client ?? client).get<
    ListWorkspacesResponses,
    ListWorkspacesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces',
    ...options,
  });

/**
 * Create a workspace
 *
 * ### Create a workspace
 * Creates a new FX workspace in the tenant.
 *
 * ### Request body
 * - **name** (required): Display name of the workspace. Must be non-empty.
 *
 * ### Notes
 * - The response returns the created workspace, including the workspace TRN. Use that TRN as the `workspaceTrn` path parameter for subsequent calls.
 * - The workspace starts empty. Add references via the type-specific endpoints (e.g. `POST .../references/mobileMappingMission`).
 */
export const createWorkspace = <ThrowOnError extends boolean = false>(
  options: Options<CreateWorkspaceData, ThrowOnError>
): RequestResult<
  CreateWorkspaceResponses,
  CreateWorkspaceErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    CreateWorkspaceResponses,
    CreateWorkspaceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete a workspace
 *
 * ### Delete a workspace
 * Permanently deletes the workspace. All WIP data owned by the workspace (settings, unpublished FX results,
 * workspace-scoped geometries) is deleted.
 *
 * ### Notes
 * - **Referenced business objects are not affected** by this operation.
 * - **Published FX results are not affected** by this operation; they continue to exist as independent BOs in Connect.
 */
export const deleteWorkspace = <ThrowOnError extends boolean = false>(
  options: Options<DeleteWorkspaceData, ThrowOnError>
): RequestResult<
  DeleteWorkspaceResponses,
  DeleteWorkspaceErrors,
  ThrowOnError
> =>
  (options.client ?? client).delete<
    DeleteWorkspaceResponses,
    DeleteWorkspaceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}',
    ...options,
  });

/**
 * Get a workspace by TRN
 *
 * ### Get a workspace
 * Returns a single workspace by its TRN.
 */
export const getWorkspace = <ThrowOnError extends boolean = false>(
  options: Options<GetWorkspaceData, ThrowOnError>
): RequestResult<GetWorkspaceResponses, GetWorkspaceErrors, ThrowOnError> =>
  (options.client ?? client).get<
    GetWorkspaceResponses,
    GetWorkspaceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}',
    ...options,
  });

/**
 * Update a workspace
 *
 * ### Update a workspace
 * Updates the workspace's metadata. Only provided fields are updated (partial update).
 *
 * ### Notes
 * - Returns the updated workspace.
 */
export const updateWorkspace = <ThrowOnError extends boolean = false>(
  options: Options<UpdateWorkspaceData, ThrowOnError>
): RequestResult<
  UpdateWorkspaceResponses,
  UpdateWorkspaceErrors,
  ThrowOnError
> =>
  (options.client ?? client).patch<
    UpdateWorkspaceResponses,
    UpdateWorkspaceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List references in a workspace
 *
 * ### List references
 * Returns a paginated list of all references the workspace holds to external business objects
 * (e.g. Mobile Mapping missions, terrestrial scans, models).
 *
 * ### Notes
 * - Each item identifies the referenced BO by its TRN; the workspace does not own the referenced data.
 * - Use **pageIndex** and **pageSize** to paginate.
 */
export const listReferences = <ThrowOnError extends boolean = false>(
  options: Options<ListReferencesData, ThrowOnError>
): RequestResult<ListReferencesResponses, ListReferencesErrors, ThrowOnError> =>
  (options.client ?? client).get<
    ListReferencesResponses,
    ListReferencesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references',
    ...options,
  });

/**
 * Add Mobile Mapping mission references
 *
 * ### Add Mobile Mapping mission references
 * Adds references to existing Mobile Mapping mission business objects. A workspace can hold multiple
 * references of each type. The referenced business objects are not modified or copied.
 *
 * ### Request body
 * - **refTrns** (required): TRNs of the existing Mobile Mapping mission business objects to reference.
 *
 * ### Notes
 * - This operation is idempotent: TRNs already referenced as a Mobile Mapping mission are skipped, not duplicated.
 * - If any TRN is already referenced with a **different** type, nothing is added and the API returns **409 Conflict**.
 * - The response returns the stored reference for every requested TRN.
 */
export const addMobileMappingMissionReferences = <
  ThrowOnError extends boolean = false,
>(
  options: Options<AddMobileMappingMissionReferencesData, ThrowOnError>
): RequestResult<
  AddMobileMappingMissionReferencesResponses,
  AddMobileMappingMissionReferencesErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    AddMobileMappingMissionReferencesResponses,
    AddMobileMappingMissionReferencesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references/mobileMappingMission',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Add terrestrial scan references
 *
 * ### Add terrestrial scan references
 * Adds references to existing terrestrial scan business objects. A workspace can hold multiple
 * references of each type. The referenced business objects are not modified or copied.
 *
 * ### Request body
 * - **refTrns** (required): TRNs of the existing terrestrial scan business objects to reference.
 *
 * ### Notes
 * - This operation is idempotent: TRNs already referenced as a terrestrial scan are skipped, not duplicated.
 * - If any TRN is already referenced with a **different** type, nothing is added and the API returns **409 Conflict**.
 * - The response returns the stored reference for every requested TRN.
 */
export const addTerrestrialScanReferences = <
  ThrowOnError extends boolean = false,
>(
  options: Options<AddTerrestrialScanReferencesData, ThrowOnError>
): RequestResult<
  AddTerrestrialScanReferencesResponses,
  AddTerrestrialScanReferencesErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    AddTerrestrialScanReferencesResponses,
    AddTerrestrialScanReferencesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references/terrestrialScan',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Add model references
 *
 * ### Add model references
 * Adds references to existing model business objects. A workspace can hold multiple
 * references of each type. The referenced business objects are not modified or copied.
 *
 * ### Request body
 * - **refTrns** (required): TRNs of the existing model business objects to reference.
 *
 * ### Notes
 * - This operation is idempotent: TRNs already referenced as a model are skipped, not duplicated.
 * - If any TRN is already referenced with a **different** type, nothing is added and the API returns **409 Conflict**.
 * - The response returns the stored reference for every requested TRN.
 */
export const addModelReferences = <ThrowOnError extends boolean = false>(
  options: Options<AddModelReferencesData, ThrowOnError>
): RequestResult<
  AddModelReferencesResponses,
  AddModelReferencesErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    AddModelReferencesResponses,
    AddModelReferencesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references/model',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Add alignment references
 *
 * ### Add alignment references
 * Adds references to existing alignment business objects. A workspace can hold multiple
 * references of each type. The referenced business objects are not modified or copied.
 *
 * ### Request body
 * - **refTrns** (required): TRNs of the existing alignment business objects to reference.
 *
 * ### Notes
 * - This operation is idempotent: TRNs already referenced as a alignment are skipped, not duplicated.
 * - If any TRN is already referenced with a **different** type, nothing is added and the API returns **409 Conflict**.
 * - The response returns the stored reference for every requested TRN.
 */
export const addAlignmentReferences = <ThrowOnError extends boolean = false>(
  options: Options<AddAlignmentReferencesData, ThrowOnError>
): RequestResult<
  AddAlignmentReferencesResponses,
  AddAlignmentReferencesErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    AddAlignmentReferencesResponses,
    AddAlignmentReferencesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references/alignment',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Add map workspace references
 *
 * ### Add map workspace references
 * Adds references to existing map workspace business objects. A workspace can hold multiple
 * references of each type. The referenced business objects are not modified or copied.
 *
 * ### Request body
 * - **refTrns** (required): TRNs of the existing map workspace business objects to reference.
 *
 * ### Notes
 * - This operation is idempotent: TRNs already referenced as a map workspace are skipped, not duplicated.
 * - If any TRN is already referenced with a **different** type, nothing is added and the API returns **409 Conflict**.
 * - The response returns the stored reference for every requested TRN.
 */
export const addMapWorkspaceReferences = <ThrowOnError extends boolean = false>(
  options: Options<AddMapWorkspaceReferencesData, ThrowOnError>
): RequestResult<
  AddMapWorkspaceReferencesResponses,
  AddMapWorkspaceReferencesErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    AddMapWorkspaceReferencesResponses,
    AddMapWorkspaceReferencesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references/mapWorkspace',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Remove a reference to an external business object
 *
 * ### Delete reference
 * Removes the workspace's reference to the specified external business object. The referenced BO itself is **not** deleted.
 *
 * ### Notes
 * - If the workspace or reference does not exist, the API returns **404 Not Found**.
 */
export const deleteReference = <ThrowOnError extends boolean = false>(
  options: Options<DeleteReferenceData, ThrowOnError>
): RequestResult<
  DeleteReferenceResponses,
  DeleteReferenceErrors,
  ThrowOnError
> =>
  (options.client ?? client).delete<
    DeleteReferenceResponses,
    DeleteReferenceErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/references/{refTrn}',
    ...options,
  });

/**
 * List breaklines
 *
 * Returns all breaklines in the workspace across every type.
 */
export const listBreaklines = <ThrowOnError extends boolean = false>(
  options: Options<ListBreaklinesData, ThrowOnError>
): RequestResult<ListBreaklinesResponses, ListBreaklinesErrors, ThrowOnError> =>
  (options.client ?? client).get<
    ListBreaklinesResponses,
    ListBreaklinesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines',
    ...options,
  });

/**
 * Delete the curb and gutter breakline
 *
 * Deletes the curb and gutter breakline and its Maps API layer.
 */
export const deleteCurbAndGutterBreakline = <
  ThrowOnError extends boolean = false,
>(
  options: Options<DeleteCurbAndGutterBreaklineData, ThrowOnError>
): RequestResult<
  DeleteCurbAndGutterBreaklineResponses,
  DeleteCurbAndGutterBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).delete<
    DeleteCurbAndGutterBreaklineResponses,
    DeleteCurbAndGutterBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/curbAndGutter',
    ...options,
  });

/**
 * Get the curb and gutter breakline
 *
 * Returns the workspace's curb and gutter breakline, including its Maps API layer.
 */
export const getCurbAndGutterBreakline = <ThrowOnError extends boolean = false>(
  options: Options<GetCurbAndGutterBreaklineData, ThrowOnError>
): RequestResult<
  GetCurbAndGutterBreaklineResponses,
  GetCurbAndGutterBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).get<
    GetCurbAndGutterBreaklineResponses,
    GetCurbAndGutterBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/curbAndGutter',
    ...options,
  });

/**
 * Create the curb and gutter breakline
 *
 * ### Create the curb and gutter breakline
 * Creates the curb and gutter breakline and provisions its Maps API layer with the
 * correct schema and style.
 *
 * Only one curb and gutter breakline can exist per workspace; a second create returns
 * **409 Conflict**.
 */
export const createCurbAndGutterBreakline = <
  ThrowOnError extends boolean = false,
>(
  options: Options<CreateCurbAndGutterBreaklineData, ThrowOnError>
): RequestResult<
  CreateCurbAndGutterBreaklineResponses,
  CreateCurbAndGutterBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    CreateCurbAndGutterBreaklineResponses,
    CreateCurbAndGutterBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/curbAndGutter',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete the road crown breakline
 *
 * Deletes the road crown breakline and its Maps API layer.
 */
export const deleteRoadCrownBreakline = <ThrowOnError extends boolean = false>(
  options: Options<DeleteRoadCrownBreaklineData, ThrowOnError>
): RequestResult<
  DeleteRoadCrownBreaklineResponses,
  DeleteRoadCrownBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).delete<
    DeleteRoadCrownBreaklineResponses,
    DeleteRoadCrownBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/roadCrown',
    ...options,
  });

/**
 * Get the road crown breakline
 *
 * Returns the workspace's road crown breakline, including its Maps API layer.
 */
export const getRoadCrownBreakline = <ThrowOnError extends boolean = false>(
  options: Options<GetRoadCrownBreaklineData, ThrowOnError>
): RequestResult<
  GetRoadCrownBreaklineResponses,
  GetRoadCrownBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).get<
    GetRoadCrownBreaklineResponses,
    GetRoadCrownBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/roadCrown',
    ...options,
  });

/**
 * Create the road crown breakline
 *
 * ### Create the road crown breakline
 * Creates the road crown breakline and provisions its Maps API layer with the
 * correct schema and style.
 *
 * Only one road crown breakline can exist per workspace; a second create returns
 * **409 Conflict**.
 */
export const createRoadCrownBreakline = <ThrowOnError extends boolean = false>(
  options: Options<CreateRoadCrownBreaklineData, ThrowOnError>
): RequestResult<
  CreateRoadCrownBreaklineResponses,
  CreateRoadCrownBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    CreateRoadCrownBreaklineResponses,
    CreateRoadCrownBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/roadCrown',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete the jersey barrier breakline
 *
 * Deletes the jersey barrier breakline and its Maps API layer.
 */
export const deleteJerseyBarrierBreakline = <
  ThrowOnError extends boolean = false,
>(
  options: Options<DeleteJerseyBarrierBreaklineData, ThrowOnError>
): RequestResult<
  DeleteJerseyBarrierBreaklineResponses,
  DeleteJerseyBarrierBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).delete<
    DeleteJerseyBarrierBreaklineResponses,
    DeleteJerseyBarrierBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/jerseyBarrier',
    ...options,
  });

/**
 * Get the jersey barrier breakline
 *
 * Returns the workspace's jersey barrier breakline, including its Maps API layer.
 */
export const getJerseyBarrierBreakline = <ThrowOnError extends boolean = false>(
  options: Options<GetJerseyBarrierBreaklineData, ThrowOnError>
): RequestResult<
  GetJerseyBarrierBreaklineResponses,
  GetJerseyBarrierBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).get<
    GetJerseyBarrierBreaklineResponses,
    GetJerseyBarrierBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/jerseyBarrier',
    ...options,
  });

/**
 * Create the jersey barrier breakline
 *
 * ### Create the jersey barrier breakline
 * Creates the jersey barrier breakline and provisions its Maps API layer with the
 * correct schema and style.
 *
 * Only one jersey barrier breakline can exist per workspace; a second create returns
 * **409 Conflict**.
 */
export const createJerseyBarrierBreakline = <
  ThrowOnError extends boolean = false,
>(
  options: Options<CreateJerseyBarrierBreaklineData, ThrowOnError>
): RequestResult<
  CreateJerseyBarrierBreaklineResponses,
  CreateJerseyBarrierBreaklineErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    CreateJerseyBarrierBreaklineResponses,
    CreateJerseyBarrierBreaklineErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/workspaces/{workspaceTrn}/breaklines/jerseyBarrier',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
